
/**
 * Enhanced Schema.org structured data utilities for advanced SEO
 */

// Enhanced Website schema with sitelinks for category navigation
export const createWebsiteSchema = (url: string = 'https://treguim.org') => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "TreguIm",
  "alternateName": "TreguIm.org",
  "url": url,
  "description": "Platforma më e madhe në Shqipëri për shitje dhe blerje online. Elektronikë, makina, shtëpi, mode dhe shumë më tepër.",
  "inLanguage": "sq-AL",
  "potentialAction": {
    "@type": "SearchAction",
    "target": `${url}/search?q={search_term_string}`,
    "query-input": "required name=search_term_string"
  },
  "sameAs": [
    "https://www.facebook.com/treguIm/",
    "https://www.instagram.com/treguim.org/",
    "https://www.tiktok.com/@treguim",
    "https://twitter.com/treguim"
  ],
  "mainEntity": {
    "@type": "ItemList",
    "name": "Kategoritë Kryesore",
    "itemListElement": [
      {
        "@type": "SiteNavigationElement",
        "position": 1,
        "name": "Elektronikë",
        "url": `${url}/search?category=elektronike`,
        "description": "Telefona, laptopë, TV dhe pajisje elektronike"
      },
      {
        "@type": "SiteNavigationElement", 
        "position": 2,
        "name": "Makina",
        "url": `${url}/search?category=makina`,
        "description": "Makina të përdorura dhe të reja për shitje"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 3,
        "name": "Shtëpi & Kopsht",
        "url": `${url}/search?category=shtepi-kopsht`,
        "description": "Mobilje, pajisje shtëpiake dhe artikel kopshti"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 4,
        "name": "Mode & Bukuri",
        "url": `${url}/search?category=mode-bukuri`,
        "description": "Veshje, këpucë dhe produkte bukurie"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 5,
        "name": "Sport & Rekreacion",
        "url": `${url}/search?category=sport`,
        "description": "Pajisje sportive dhe për kohën e lirë"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 6,
        "name": "Pasuri të Paluajtshme",
        "url": `${url}/search?category=pasuri-paluajtshme`,
        "description": "Shtëpi, apartamente dhe tokë për shitje"
      }
    ]
  }
});

// Enhanced Organization schema with detailed business information
export const createOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "TreguIm",
  "legalName": "TreguIm Sh.p.k.",
  "url": "https://treguim.org",
  "logo": {
    "@type": "ImageObject",
    "url": "https://treguim.org/logo512.png",
    "width": 512,
    "height": 512,
    "caption": "TreguIm Logo"
  },
  "image": "https://treguim.org/sharing-default.jpg",
  "description": "TreguIm është platforma më e madhe për blerjen dhe shitjen e produkteve në Shqipëri. Marketplace i besueshëm me mijëra produkte dhe shërbime të sigurta.",
  "foundingDate": "2018",
  "founders": [
    {
      "@type": "Person",
      "name": "TreguIm Team"
    }
  ],
  "numberOfEmployees": {
    "@type": "QuantitativeValue",
    "value": "10-50"
  },
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Rruga e Kavajes",
    "addressLocality": "Tiranë",
    "addressRegion": "Tiranë",
    "postalCode": "1001",
    "addressCountry": {
      "@type": "Country",
      "name": "Albania",
      "identifier": "AL"
    }
  },
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "telephone": "+355-69-123-4567",
      "contactType": "customer service",
      "email": "info@treguim.org",
      "availableLanguage": ["Albanian", "English"],
      "areaServed": "AL"
    },
    {
      "@type": "ContactPoint",
      "contactType": "technical support",
      "email": "support@treguim.org",
      "availableLanguage": ["Albanian", "English"],
      "areaServed": "AL"
    }
  ],
  "sameAs": [
    "https://www.facebook.com/treguIm/",
    "https://www.instagram.com/treguim.org/",
    "https://www.tiktok.com/@treguim",
    "https://twitter.com/treguim"
  ],
  "serviceArea": {
    "@type": "Country",
    "name": "Albania",
    "identifier": "AL"
  },
  "knowsAbout": [
    "E-commerce",
    "Online Marketplace",
    "Digital Commerce",
    "Albanian Market",
    "Online Shopping",
    "Product Sales"
  ],
  "award": [
    "Best Albanian Marketplace 2023",
    "Most Trusted Platform Albania"
  ]
});

// Web Application schema for PWA installation
export const createWebApplicationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "TreguIm",
  "description": "Aplikacioni i TreguIm për blerje dhe shitje të shpejtë. Instaloni aplikacionin për qasje më të lehtë.",
  "url": "https://treguim.org",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web Browser, iOS, Android",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "EUR"
  },
  "screenshot": {
    "@type": "ImageObject",
    "url": "https://treguim.org/sharing-default.jpg",
    "caption": "TreguIm App Screenshot"
  },
  "installUrl": "https://treguim.org/?install=true",
  "permissions": "Kamera, Vendndodhja (opsionale)",
  "memoryRequirements": "50MB",
  "storageRequirements": "100MB",
  "availableOnDevice": ["Desktop", "Mobile"],
  "featureList": [
    "Shpallje falas",
    "Chat i integruar",
    "Kërkime të avancuara", 
    "Njoftimet push",
    "Offline reading",
    "Favorite të ruajtura"
  ]
});

// Enhanced Product schema for better SEO
export const createProductSchema = (product: any, url: string) => {
  if (!product) return null;
  
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.title,
    "description": product.description,
    "sku": `TREGU-${product.id}`,
    "mpn": `TREGU-${product.id}`,
    "url": url,
    "identifier": {
      "@type": "PropertyValue",
      "name": "TreguIm ID",
      "value": product.id.toString()
    }
  };
  
  // Add images if available
  if (product.images?.length) {
    schema.image = product.images.map((img: string) => ({
      "@type": "ImageObject",
      "url": img.startsWith('http') ? img : new URL(img, window.location.origin).toString(),
      "caption": product.title
    }));
  }
  
  // Add brand/seller info
  if (product.seller?.full_name || product.seller?.store_name) {
    schema.brand = {
      "@type": product.seller?.is_store ? "Organization" : "Brand",
      "name": product.seller?.store_name || product.seller?.full_name
    };
  }
  
  // Enhanced offer details
  schema.offers = {
    "@type": "Offer",
    "@id": `${url}#offer`,
    "url": url,
    "priceCurrency": product.currency === 'ALL' ? "ALL" : "EUR",
    "price": product.price,
    "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1))
      .toISOString().split('T')[0],
    "itemCondition": `https://schema.org/${product.condition === 'new' ? 'NewCondition' : 'UsedCondition'}`,
    "availability": product.stock > 0 ? 
      "https://schema.org/InStock" : 
      "https://schema.org/OutOfStock",
    "businessFunction": "http://purl.org/goodrelations/v1#Sell"
  };
  
  // Add seller information
  if (product.seller) {
    schema.offers.seller = {
      "@type": product.seller.is_store ? "Organization" : "Person",
      "name": product.seller.store_name || product.seller.full_name || product.seller.username
    };
  }
  
  // Add location if available
  if (product.location) {
    schema.offers.availableAtOrFrom = {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": product.location,
        "addressCountry": "AL"
      }
    };
  }
  
  // Add category
  if (product.category_name) {
    schema.category = product.category_name;
    schema.additionalType = `https://treguim.org/category/${product.category_name.toLowerCase()}`;
  }
  
  // Add ratings if available
  if (product.avg_rating && product.rating_count) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": product.avg_rating,
      "reviewCount": product.rating_count,
      "bestRating": 5,
      "worstRating": 1
    };
  }
  
  // Add vehicle-specific details if it's a vehicle
  if (product.make && product.model) {
    schema["@type"] = ["Product", "Vehicle"];
    schema.additionalProperty = [
      {
        "@type": "PropertyValue",
        "name": "Make",
        "value": product.make
      },
      {
        "@type": "PropertyValue", 
        "name": "Model",
        "value": product.model
      }
    ];
    
    if (product.year) {
      schema.additionalProperty.push({
        "@type": "PropertyValue",
        "name": "Year",
        "value": product.year.toString()
      });
    }
    
    if (product.mileage) {
      schema.additionalProperty.push({
        "@type": "PropertyValue",
        "name": "Mileage", 
        "value": `${product.mileage} km`
      });
    }
  }
  
  return schema;
};

// Local business schema for contact page
export const createLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "TreguIm",
  "image": "https://treguim.org/logo512.png",
  "url": "https://treguim.org",
  "telephone": "+355 69 123 4567",
  "email": "info@treguim.org",
  "priceRange": "Falas - €€€",
  "paymentAccepted": ["PayPal", "Credit Card", "Bank Transfer"],
  "currenciesAccepted": ["EUR", "ALL"],
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Rruga e Kavajes",
    "addressLocality": "Tiranë",
    "postalCode": "1001",
    "addressCountry": "AL"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "41.3275",
    "longitude": "19.8187"
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday", 
        "Wednesday",
        "Thursday",
        "Friday"
      ],
      "opens": "09:00",
      "closes": "17:00"
    }
  ],
  "serviceArea": {
    "@type": "Country",
    "name": "Albania"
  }
});

// BreadcrumbList schema for navigation
export const createBreadcrumbSchema = (items: Array<{name: string, url: string}>) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
};

// Store/Organization schema
export const createStoreSchema = (store: any, url: string) => {
  if (!store) return null;
  
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": store.store_name || store.username,
    "url": url,
    "description": store.store_description || `Dyqan online ${store.store_name || store.username} në TreguIm`
  };
  
  // Add logo/image
  if (store.profile_image) {
    schema.logo = store.profile_image.startsWith('http') ? 
      store.profile_image : 
      new URL(store.profile_image, window.location.origin).toString();
  }
  
  // Add banner image
  if (store.store_banner_url) {
    schema.image = store.store_banner_url.startsWith('http') ? 
      store.store_banner_url : 
      new URL(store.store_banner_url, window.location.origin).toString();
  }
  
  // Add location if available
  if (store.location) {
    schema.address = {
      "@type": "PostalAddress",
      "addressLocality": store.location,
      "addressCountry": "AL"
    };
  }
  
  // Add founding date
  if (store.created_at) {
    schema.foundingDate = new Date(store.created_at).toISOString().split('T')[0];
  }
  
  return schema;
};

// Enhanced FAQ schema for homepage
export const createFAQSchema = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Si mund të postoj një shpallje falas në TreguIm?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Regjistrohuni në platformë, shkoni te 'Posto Shpallje' dhe plotësoni formularin me detajet e produktit tuaj. Shpalljet janë falas për të gjithë përdoruesit."
      }
    },
    {
      "@type": "Question",
      "name": "A është e sigurt të blej në TreguIm?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Po, TreguIm ofron një platformë të sigurt për blerje. Rekomandojmë gjithmonë të kontaktoni shitësin dhe të verifikoni produktin para blerjes."
      }
    },
    {
      "@type": "Question",
      "name": "Si funksionojnë shpalljet Premium?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Shpalljet Premium shfaqen në krye të rezultateve të kërkimit dhe kanë më shumë dukshmëri. Mund t'i blini për vetëm 5€ për shpallje."
      }
    },
    {
      "@type": "Question",
      "name": "Cilat janë format e pagesës të pranuara?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Pranojmë pagesa me PayPal, karta krediti dhe transferta bankare për shërbime Premium dhe blerje online."
      }
    },
    {
      "@type": "Question",
      "name": "Si të instloj aplikacionin e TreguIm?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Hapni TreguIm në shfletuesin tuaj dhe klikoni butonin 'Instalo App' kur shfaqet. Aplikacioni funksionon në të gjitha pajisjet."
      }
    },
    {
      "@type": "Question",
      "name": "A mund të shes në TreguIm nëse jam jashtë Shqipërisë?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Po, mund të shisni produkte edhe nga jashtë, por duhet të specifikoni metodat e dërgesës dhe koston për blerësit në Shqipëri."
      }
    }
  ]
});

// Enhanced Featured Products schema
export const createFeaturedProductsSchema = (products: any[]) => {
  if (!products || products.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Produktet e Zgjedhura - TreguIm",
    "description": "Produktet më të populluara dhe të rekomanduara në TreguIm",
    "numberOfItems": products.length,
    "itemListElement": products.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.title,
        "description": product.description,
        "image": product.images?.[0] || "https://treguim.org/logo512.png",
        "url": `https://treguim.org/product/${product.id}`,
        "offers": {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": product.currency || "EUR",
          "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
        }
      }
    }))
  };
};

// SiteNavigationElement schema for main categories
export const createSiteNavigationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SiteNavigationElement",
  "name": "Navigimi Kryesor TreguIm",
  "url": "https://treguim.org",
  "hasPart": [
    {
      "@type": "SiteNavigationElement",
      "name": "Elektronikë",
      "url": "https://treguim.org/search?category=elektronike",
      "description": "Telefona, laptopë, TV dhe pajisje elektronike"
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Makina",
      "url": "https://treguim.org/search?category=makina",
      "description": "Makina të përdorura dhe të reja"
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Shtëpi & Kopsht", 
      "url": "https://treguim.org/search?category=shtepi-kopsht",
      "description": "Mobilje dhe pajisje shtëpiake"
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Mode & Bukuri",
      "url": "https://treguim.org/search?category=mode-bukuri", 
      "description": "Veshje, këpucë dhe bukuri"
    }
  ]
});

// App Installation Action schema
export const createAppInstallActionSchema = () => ({
  "@context": "https://schema.org",
  "@type": "InstallAction",
  "target": {
    "@type": "EntryPoint",
    "urlTemplate": "https://treguim.org/?install=true",
    "actionPlatform": [
      "http://schema.org/DesktopWebPlatform",
      "http://schema.org/MobileWebPlatform",
      "http://schema.org/IOSPlatform", 
      "http://schema.org/AndroidPlatform"
    ]
  },
  "result": {
    "@type": "WebApplication",
    "name": "TreguIm",
    "operatingSystem": "Web Browser"
  }
});
